import {
  BillingSummary,
  DirectDebitSummary,
  InboundSwitchTracker,
} from '@ovotech/energy-cx';
import { Col, Margin, Modal, Row, VisuallyHidden } from '@ovotech/nebula';
import { Sandwich, useAnalytics } from '@ovotech/ui-tools';

import React, { Fragment, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom-v5';
import { UsageAndMeterReadWidgets } from './components/UsageRow/UsageAndMeterReadRow';
import { MajorIncidentGeneralBanner } from './components/mi-banners';
import {
  ActionCard,
  DisplayFlex,
  ErrorBoundary,
  PageWithNavigation,
  Section,
} from '@/src/components';
import { NestedDisplayFlex } from '@/src/components/layout';
import { HOME_SCREEN } from '@/src/constants/analytics-subjects';
import {
  ACCOUNT_INFO_ERROR,
  ACTION_CENTRE_ERROR,
  BILLING_SUMMARY_WIDGET_ERROR,
  DD_WIDGET_ERROR,
} from '@/src/constants/error-section-ids';
import { sandwichIds } from '@/src/constants/sandwich-ids';
import { TIMELINE_SECTION_ID } from '@/src/constants/sectionIds';
import { StyledModal } from '@/src/components/HighBalanceModal/HighBalanceModal';
import { HasDDModalContent } from '@/src/components/HighBalanceModal/HighBalanceModalContent/HasDDModalContent';
import { NoDDModalContent } from '@/src/components/HighBalanceModal/HighBalanceModalContent/NoDDModalContent';
import { BalanceSummary } from '@/src/components/BalanceSummary/BalanceSummary';
import { useBalance } from '@/src/pages/Payments/hooks/useBalance';
import styled from 'styled-components';
import { useDecision } from '@/src/utils/optimizely';
import { BEYOND_WEB_PRESENCE } from '@/src/constants/feature-flags';
import { GetBeyondBanner } from '@/src/components/Banners/GetBeyondBanner';
import { BeyondAccountStatusGraphqlResponse } from '@/src/types/Response';
import { useBeyondAccountFlags } from '@/src/hooks/useBeyondAccountFlags/useBeyondAccountFlags';
import { testIds } from './testIds';

const StyledSVGSizeOverride = styled.div`
  svg {
    max-width: unset;
  }
`;

const InboundSwitchTrackerWrapper = styled.div`
  margin-top: ${({ theme }) => theme.space[8]};
`;

enum HighBalanceModal {
  None = '',
  HasDD = 'modal=balance_review_has_dd',
  NoDD = 'modal=balance_review_no_dd',
}

interface InLifeHomePageProps {
  allSuppliesArePastWindow: boolean;
  beyondAccountStatusData?: BeyondAccountStatusGraphqlResponse;
}

export const PAYMHomePage = (props: InLifeHomePageProps) => {
  const { allSuppliesArePastWindow, beyondAccountStatusData } = props;

  const { balanceVariation, isFetched } = useBalance();

  const history = useHistory();
  const [beyondWebPresence] = useDecision(BEYOND_WEB_PRESENCE) || [
    { variationKey: null, enabled: false },
  ];

  const { beyondStatus } = useBeyondAccountFlags();

  const [modalIsOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<HighBalanceModal>(
    HighBalanceModal.None,
  );

  const openModal = () => {
    setIsOpen(true);
    document.documentElement.setAttribute('style', 'overflow-y: hidden;');
  };

  const closeModal = () => {
    setIsOpen(false);
    history.push({
      search: ``,
    });
    document.documentElement.setAttribute('style', 'overflow-y: scroll;');
  };

  const generateModalContent = () => {
    if (history.location.search.includes(HighBalanceModal.HasDD)) {
      openModal();
      setModalContent(HighBalanceModal.HasDD);
    } else if (history.location.search.includes(HighBalanceModal.NoDD)) {
      openModal();
      setModalContent(HighBalanceModal.NoDD);
    } else {
      closeModal();
      setModalContent(HighBalanceModal.None);
    }
  };

  useEffect(() => {
    generateModalContent();
  }, [history.location.search]);

  const { dispatch } = useAnalytics();

  useEffect(() => {
    if (isFetched) {
      dispatch({
        name: HOME_SCREEN,
        type: 'page',
        properties: {
          balanceComponentTest: balanceVariation,
          ...(beyondWebPresence.enabled && {
            'Test variant':
              beyondWebPresence.variationKey === 'rewards'
                ? 'Rewards'
                : 'Beyond',
          }),
          ...(beyondStatus && { 'Beyond Status': beyondStatus }),
        },
      });
    }
    // we only want to call dispatch once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  return (
    <PageWithNavigation title="Home">
      <MajorIncidentGeneralBanner />

      {allSuppliesArePastWindow ? (
        <Sandwich id={sandwichIds.homepage.actionCentre}>
          <ErrorBoundary errorId={ACTION_CENTRE_ERROR}>
            <ActionCard utmSource="action-center" />
          </ErrorBoundary>
        </Sandwich>
      ) : (
        <InboundSwitchTrackerWrapper data-testid="orion-wrapper-inbound-switch-tracker">
          <InboundSwitchTracker />
        </InboundSwitchTrackerWrapper>
      )}

      <div>
        <StyledModal
          as={Modal}
          isOpen={modalIsOpen}
          onClose={closeModal}
          title={<VisuallyHidden>Tariff Modal</VisuallyHidden>}
        >
          {modalContent === HighBalanceModal.HasDD ? (
            <HasDDModalContent />
          ) : (
            <NoDDModalContent />
          )}
        </StyledModal>
      </div>

      <Section id={TIMELINE_SECTION_ID}>
        <Fragment>
          <VisuallyHidden>
            <h1 data-testid={testIds.paym.heading}>Home</h1>
          </VisuallyHidden>
          <StyledSVGSizeOverride>
            <Margin bottom={6}>
              <Row isNested>
                <Col medium={6} as={NestedDisplayFlex}>
                  <ErrorBoundary errorId={ACCOUNT_INFO_ERROR}>
                    <BalanceSummary />
                  </ErrorBoundary>
                </Col>

                <Col medium={6} as={DisplayFlex}>
                  <ErrorBoundary errorId={DD_WIDGET_ERROR}>
                    <DirectDebitSummary />
                  </ErrorBoundary>
                </Col>
              </Row>
              <UsageAndMeterReadWidgets />
            </Margin>
          </StyledSVGSizeOverride>
        </Fragment>
        <Sandwich id={sandwichIds.homepage.accountTimeline}>
          <ErrorBoundary errorId={BILLING_SUMMARY_WIDGET_ERROR}>
            <BillingSummary />
          </ErrorBoundary>
          {beyondAccountStatusData && (
            <GetBeyondBanner
              beyondAccountStatus={beyondAccountStatusData.beyondAccountStatus}
            />
          )}
        </Sandwich>
      </Section>
    </PageWithNavigation>
  );
};
